import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  ListItemAvatar,
  IconButton,
  List,
  Avatar,
  ListItem,
  Paper,
  ListItemText,
  Typography,
  Modal,
  TextField,
  InputAdornment,
  Box,
  useTheme,
} from "@mui/material";
import { GridSearchIcon } from "@mui/x-data-grid";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import CrossIcon from "src/icons/CrossIcon";
import { useAppDispatch } from "src/store/store";
import { TargetOrderHoldingsSkeleton } from "src/components/common/Skeletons";
import { TokenNameDisplay } from "src/components/dca/styled-components/DcaFields";
import SmallArrowDropDownIcon from "src/icons/SmallArrowDropDownIcon";
import { setSellCurrency, useDcaSlice } from "src/store/features/dca.slice";
import { useUserSlice } from "src/store/features/user.slice";
import { TargetOrderStyles } from "../trade/styles/TargetOrder.style";
import { useGetUserFiatHoldingsQuery } from "src/store/features/api/generated";
import { StyledLabel } from "./styled-components/TableStyledComponents";
import { trackEvent } from "src/Amplitude";

export const SellFiatCurrencyModal = () => {
  const classes = TargetOrderStyles();
  const {
    defaultCurrency: { code: defaultCurrencyCode },
  } = useUserSlice();
  const dispatch = useAppDispatch();
  const { sellCurrency } = useDcaSlice();
  const { data: fiatCurrency = [], isLoading } = useGetUserFiatHoldingsQuery();
  const [modal, setModal] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const theme = useTheme();
  useEffect(() => {
    dispatch(setSellCurrency(defaultCurrencyCode));
  }, [defaultCurrencyCode]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
    trackEvent("Search sell currency", { value: e.target.value }, "DCA");
  };

  const handleCoinSelect = (selectedsellCurrency: string) => {
    dispatch(setSellCurrency(selectedsellCurrency));
    handleModalToggle();
    trackEvent("Changed sell currency", { selectedsellCurrency }, "DCA");
  };
  const handleModalToggle = () => {
    setModal((prev) => !prev);
  };

  return (
    <>
      <Box
        onClick={handleModalToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          cursor: "pointer",
        }}>
        <CountryOrCryptoFlag name={sellCurrency} size={20} />
        <TokenNameDisplay>{sellCurrency}</TokenNameDisplay>
        <SmallArrowDropDownIcon color={theme.palette.mode === "light" ? undefined : "#fff"} />
      </Box>
      <Modal open={modal} onClose={handleModalToggle}>
        <Paper elevation={2} className={classes.paperContainer}>
          <Grid container alignItems="center">
            <Grid item xs={10}>
              <Typography color="textSecondary" className={classes.title}>
                {`Select Currency to Sell`}
              </Typography>
            </Grid>
            <Grid
              onClick={handleModalToggle}
              item
              xs={2}
              container
              justifyContent="center"
              className={classes.closeContainer}>
              <IconButton>
                <CrossIcon height="16px" width="16px" />
              </IconButton>
            </Grid>
          </Grid>
          <Grid container justifyContent="center">
            <TextField
              id="input-with-icon-textfield"
              placeholder="Search"
              onChange={handleSearchChange}
              sx={{
                width: "85%",
                "& .MuiOutlinedInput-root": {
                  height: "36px",
                  border: "solid 1px #d1d4d6",
                  borderRadius: "30px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GridSearchIcon sx={{ color: "#d1d4d6" }} />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid container>
            <List className={classes.list}>
              {isLoading && !fiatCurrency.length && <TargetOrderHoldingsSkeleton />}
              {fiatCurrency.length === 0 && !isLoading && (
                <Box mx={3}>
                  <StyledLabel>
                    {`Your balance appears to be low for this transaction. To continue, please deposit more funds. `}
                  </StyledLabel>
                </Box>
              )}
              {fiatCurrency
                ?.filter(
                  (el) =>
                    el.name?.toLowerCase().includes(searchVal?.toLowerCase() ?? "") ||
                    el.code?.toLowerCase().includes(searchVal?.toLowerCase() ?? ""),
                )
                .map((el: any, idx: any) => (
                  <Button
                    disableRipple
                    fullWidth
                    key={idx}
                    onClick={() => handleCoinSelect(el.code)}>
                    <Grid container>
                      <ListItem data-sellCurrency={el.sellCurrency} data-name={el.name}>
                        <ListItemAvatar>
                          <Avatar style={{ backgroundColor: "transparent" }}>
                            <CountryOrCryptoFlag name={el.logoUrl || el.code} size={35} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={el.name}
                          secondaryTypographyProps={{
                            color: "#9ca3a8 ",
                          }}
                          primaryTypographyProps={{
                            fontSize: "14px",
                            fontWeight: 600,
                          }}
                          sx={{
                            textTransform: "none",
                            flexBasis: "auto",
                          }}
                        />
                        <ListItemText
                          primary={el.sellCurrency}
                          secondary={el.available}
                          secondaryTypographyProps={{
                            color: "#9ca3a8 ",
                          }}
                          primaryTypographyProps={{
                            fontSize: "14px",
                            fontWeight: 700,
                          }}
                          sx={{
                            display: "flex",
                            flexDirection: "row-reverse",
                            columnGap: "10px",
                          }}
                        />
                      </ListItem>
                    </Grid>
                  </Button>
                ))}
            </List>
          </Grid>
        </Paper>
      </Modal>
    </>
  );
};
export default SellFiatCurrencyModal;
